import { hydraUiApi } from '@/services/api';
import { isEmptyString } from '@/utils/string';

export interface InboxLinkResult {
  email: string;
  provider: string;
  label: string;
  url: string;
}

// The "from" address used for signup verification emails,
// configured on the "signup_verification" transactional message in Customer.io.
// Subject: "Action required to verify your Customer.io account".
const VERIFICATION_SENDER = 'win@customer.io';
const VERIFICATION_SENDER_DOMAIN = 'customer.io';

const ENCODED_VERIFICATION_SENDER = encodeURIComponent(VERIFICATION_SENDER);

/**
 * Build a sniper link URL for the given provider and user email.
 *
 * Sniper links deep-link into the user's inbox with search parameters that
 * surface only the verification email, cutting through spam folders and
 * multi-account confusion. See https://growth.design/sniper-link
 */
const SNIPER_URL_BUILDERS: Record<string, (encodedEmail: string) => string> = {
  google: () =>
    `https://mail.google.com/mail/u/0/#search/from%3A(${ENCODED_VERIFICATION_SENDER})+in%3Aanywhere+newer_than%3A1d`,
  microsoft: (encodedEmail) => `https://outlook.live.com/mail/?login_hint=${encodedEmail}`,
  microsoft365: (encodedEmail) => `https://outlook.office365.com/mail/?login_hint=${encodedEmail}`,
  yahoo: () => `https://mail.yahoo.com/d/search/keyword=from%253A${VERIFICATION_SENDER_DOMAIN}`,
  aol: () => `https://mail.aol.com/d/search/keyword=from%253A${VERIFICATION_SENDER_DOMAIN}`,
  protonmail: () => `https://mail.proton.me/u/0/all-mail#from=${VERIFICATION_SENDER}`,
  apple: () => 'https://www.icloud.com/mail',
  zoho: () => 'https://mail.zoho.com',
  fastmail: () => `https://app.fastmail.com/mail/search:from%3A${ENCODED_VERIFICATION_SENDER}/`,
  yandex: () => 'https://mail.yandex.com',
  godaddy: () => 'https://email.godaddy.com',
  rackspace: () => 'https://apps.rackspace.com',
};

function buildSniperUrl(provider: string, email: string): string {
  const builder = SNIPER_URL_BUILDERS[provider];
  if (builder == null) {
    return '';
  }
  return builder(encodeURIComponent(email));
}

/**
 * Resolve the given email address to a provider-specific "sniper link" inbox URL.
 *
 * Delegates provider detection to the `/v1/inbox_link` services endpoint, which performs
 * an MX lookup. This handles consumer mailboxes (gmail.com, outlook.com, ...) and custom
 * domains hosted on Google Workspace / Microsoft 365 / etc. — the latter is the common
 * case for business signups, which a static domain-to-provider map would miss.
 */
export async function getInboxLinkAsync(email: string): Promise<InboxLinkResult | null> {
  if (isEmptyString(email)) {
    return null;
  }

  try {
    const data = await hydraUiApi.mxLookup.inboxLink({ body: { email } });

    if (data.found !== true || data.provider == null || data.label == null || data.email == null) {
      return null;
    }

    // The backend returns 'yahoo' for AOL domains since they share MX infrastructure.
    // Distinguish AOL by domain so we build the correct sniper link URL and label.
    const isAol = data.email.split('@')[1]?.toLowerCase() === 'aol.com';
    const provider = isAol ? 'aol' : data.provider;
    const label = isAol ? 'AOL Mail' : data.label;
    const url = buildSniperUrl(provider, data.email);
    if (isEmptyString(url)) {
      return null;
    }

    return {
      email: data.email,
      provider,
      label,
      url,
    };
  } catch {
    return null;
  }
}
